import { DetailSections } from '../components/ui/DetailSections';
import { detailSections } from '../config/detailSections';
import { useDetailSection } from '../hooks/useDetailSection';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  KanbanSquare,
  BookOpen,
  ArrowLeft,
  Loader,
  AlertCircle,
  FileText,
  MessageSquare,
  Tag,
  Gift,
  Server,
  Calendar,
  Activity,
  Pencil,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  Plus,
  X,
  UserPlus,
  ChevronDown,
  Search,
  UsersRound,
  FileClock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useIsMobile } from '../hooks/useIsMobile';
import { cn } from '../utils/cn';
import { getPartnershipPerms } from '../utils/partnershipPermissions';
import { canViewModpackConfigs } from '../utils/modpackConfigPermissions';
import { partnershipsApiService } from '../services/partnershipsApi';
import { usernameCacheService, useUsernameCacheVersion } from '../services/usernameCache';
import { useDebounce } from '../hooks/useDebounce';
import { useStaffListSearch } from '../hooks/useStaffListSearch';
import {
  PartnershipFull,
  PartnershipStatus,
  PartnershipTag,
  PARTNERSHIP_STATUSES,
} from '../types/partnership';
import { PartnershipStatusBadge } from '../components/partnerships/PartnershipStatusBadge';
import { PartnershipPriorityBadge } from '../components/partnerships/PartnershipPriorityBadge';
import { PartnershipNotesSection } from '../components/partnerships/PartnershipNotesSection';
import { PartnershipCommentsSection } from '../components/partnerships/PartnershipCommentsSection';
import { PartnershipCodesSection } from '../components/partnerships/PartnershipCodesSection';
import { PartnershipIncentivesSection } from '../components/partnerships/PartnershipIncentivesSection';
import { PartnershipServersSection } from '../components/partnerships/PartnershipServersSection';
import { PartnershipCalendarSection } from '../components/partnerships/PartnershipCalendarSection';
import { PartnershipActivityLog } from '../components/partnerships/PartnershipActivityLog';
import { PartnerSummaryCard } from '../components/partners/PartnerSummaryCard';
import { PartnerReportList } from '../components/partners/PartnerReportList';
import { PartnerNoticeBar } from '../components/partners/PartnerNoticeBar';
import type { PartnerNotice } from '../utils/partnerSync';
import { partnerPagePath } from '../utils/partnerLinks';
import { KanbanLinkedCardsSection } from '../components/kanban/KanbanLinkedCardsSection';
import { EditPartnershipModal } from '../components/partnerships/EditPartnershipModal';
import { CreatorDetailModal } from '../components/partnerships/CreatorDetailModal';
import { ModpackDetailModal } from '../components/partnerships/ModpackDetailModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { PlayerProfile } from '../components/PlayerProfile';
import { CreatorProfile } from '../components/CreatorProfile';
import { format, formatDistanceToNow } from 'date-fns';
import { ApogeePackEnrichment } from '../components/apogee/ApogeePackEnrichment';
import { PageHeader } from '../components/ui/PageHeader';
import { SectionHeader } from '../components/ui/SectionHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { SkeletonLoader } from '../components/SkeletonLoader';

type TabId = 'overview' | 'partner' | 'updates' | 'workspaces' | 'notes' | 'comments' | 'codes' | 'incentives' | 'servers' | 'calendar' | 'activity';

const TABS: { id: TabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'overview', label: 'Brief & dates', icon: BookOpen },
  { id: 'partner', label: 'Team & Discord', icon: UsersRound },
  { id: 'updates', label: 'Updates', icon: FileClock },
  { id: 'notes', label: 'Notes', icon: FileText },
  { id: 'comments', label: 'Comments', icon: MessageSquare },
  { id: 'codes', label: 'Codes', icon: Tag },
  { id: 'incentives', label: 'Incentives', icon: Gift },
  { id: 'servers', label: 'Servers', icon: Server },
  { id: 'calendar', label: 'Calendar', icon: Calendar },
  { id: 'workspaces', label: 'Kanban cards', icon: KanbanSquare },
  { id: 'activity', label: 'Activity', icon: Activity },
];

export const PartnershipDetailView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const isMobile = useIsMobile();

  const isDev = import.meta.env.DEV;

  const [partnership, setPartnership] = useState<PartnershipFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useDetailSection(TABS, 'notes');
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  const [showCreatorDetail, setShowCreatorDetail] = useState(false);
  const [showModpackDetail, setShowModpackDetail] = useState(false);
  const [partnerNotice, setPartnerNotice] = useState<PartnerNotice | null>(null);

  // Tag management
  const [allTags, setAllTags] = useState<PartnershipTag[]>([]);
  const [showTagPicker, setShowTagPicker] = useState(false);
  const [tagActionLoading, setTagActionLoading] = useState<string | null>(null);

  // Priority quick-change
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [priorityChanging, setPriorityChanging] = useState(false);

  // Assignee quick-change
  const [showAssigneeInput, setShowAssigneeInput] = useState(false);
  const [assigneeSearchQuery, setAssigneeSearchQuery] = useState('');
  const [assigneeChanging, setAssigneeChanging] = useState(false);
  const debouncedAssigneeSearch = useDebounce(assigneeSearchQuery, 300);
  const {
    matches: assigneeStaffMatches,
    searching: assigneeSearchLoading,
    error: assigneeStaffSearchError,
  } = useStaffListSearch({
    debouncedQuery: debouncedAssigneeSearch,
    enabled: showAssigneeInput,
  });
  const assigneeUsernameCacheVersion = useUsernameCacheVersion();
  const assigneeSearchResults = useMemo(
    () =>
      assigneeStaffMatches.map((s) => ({
        uuid: s.minecraft_uuid,
        name:
          usernameCacheService.getName(s.minecraft_uuid) ?? s.minecraft_uuid,
      })),
    [assigneeStaffMatches, assigneeUsernameCacheVersion]
  );

  const perms = useMemo(() => getPartnershipPerms(identity, isDev), [identity, isDev]);
  const canEdit = perms.canEdit;
  const canDelete = perms.canAdmin;

  const canCreate = canEdit;

  useDocumentTitle(partnership ? `Partnership: ${partnership.modpack?.name || partnership.creator?.name || partnership.id}` : 'Partnership Details');

  const loadPartnership = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await partnershipsApiService.getPartnershipById(id);
      setPartnership(data);

      // Prefetch usernames
      const uuids = new Set<string>();
      if (data.assigned_to) uuids.add(data.assigned_to);
      if (data.created_by) uuids.add(data.created_by);
      if (data.updated_by) uuids.add(data.updated_by);
      data.latest_activity?.forEach(a => uuids.add(a.actor_id));
      if (uuids.size > 0) {
        usernameCacheService.prefetchUsernames(Array.from(uuids));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load partnership');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadPartnership();
  }, [loadPartnership]);

  useEffect(() => {
    partnershipsApiService.getAllTags().then(setAllTags).catch(console.error);
  }, []);

  useEffect(() => {
    const handleClick = () => {
      setShowTagPicker(false);
      setShowPriorityDropdown(false);
      setShowStatusDropdown(false);
    };
    if (showTagPicker || showPriorityDropdown || showStatusDropdown) {
      const timer = setTimeout(() => {
        document.addEventListener('click', handleClick, { once: true });
      }, 0);
      return () => {
        clearTimeout(timer);
        document.removeEventListener('click', handleClick);
      };
    }
  }, [showTagPicker, showPriorityDropdown, showStatusDropdown]);

  const handleAddTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await partnershipsApiService.addTagToPartnership(id, tagId);
      await loadPartnership();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    if (!id) return;
    setTagActionLoading(tagId);
    try {
      await partnershipsApiService.removeTagFromPartnership(id, tagId);
      await loadPartnership();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove tag');
    } finally {
      setTagActionLoading(null);
    }
  };

  const handlePriorityChange = async (newPriority: number) => {
    if (!id || !partnership) return;
    setPriorityChanging(true);
    setShowPriorityDropdown(false);
    try {
      await partnershipsApiService.changePartnershipPriority(id, newPriority);
      await loadPartnership();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change priority');
    } finally {
      setPriorityChanging(false);
    }
  };

  const handleAssigneeChange = async (newAssignee: string | null) => {
    if (!id) return;
    setAssigneeChanging(true);
    try {
      await partnershipsApiService.assignPartnership(id, newAssignee);
      setShowAssigneeInput(false);
      setAssigneeSearchQuery('');
      await loadPartnership();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change assignee');
    } finally {
      setAssigneeChanging(false);
    }
  };

  const handleStatusChange = async (newStatus: PartnershipStatus) => {
    if (!partnership || !id) return;
    setStatusChanging(true);
    setShowStatusDropdown(false);
    try {
      let reason: string | undefined;
      if (newStatus === 'declined') {
        reason = prompt('Enter decline reason:') || undefined;
        if (reason === undefined && newStatus === 'declined') {
          setStatusChanging(false);
          return;
        }
      }
      await partnershipsApiService.changePartnershipStatus(id, newStatus, reason);
      await loadPartnership();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to change status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setIsDeleting(true);
    try {
      await partnershipsApiService.deletePartnership(id);
      navigate('/partnerships');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete partnership');
    } finally {
      setIsDeleting(false);
      setIsDeleteOpen(false);
    }
  };

  const handleCopyId = () => {
    if (!id) return;
    navigator.clipboard.writeText(id);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return '—';
    const ms = timestamp < 1e12 ? timestamp * 1000 : timestamp;
    return format(new Date(ms), 'MMM d, yyyy');
  };

  if (loading) {
    return (
      <div className="space-y-6" aria-busy="true">
        {/* Header skeleton */}
        <div className="space-y-3">
          <SkeletonLoader className="h-3 w-24" />
          <div className="flex items-center gap-3">
            <SkeletonLoader className="w-10 h-10 rounded-md" />
            <SkeletonLoader className="h-7 w-64 max-w-full" />
            <SkeletonLoader className="h-5 w-20 rounded-full" />
            <SkeletonLoader className="h-5 w-10 rounded-full" />
          </div>
          <SkeletonLoader className="h-4 w-48" />
        </div>
        {/* Info cards */}
        <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
          {[0, 1].map((i) => (
            <div key={i} className="glass-card p-4 md:p-5 space-y-3">
              <SkeletonLoader className="h-4 w-28" />
              <SkeletonLoader className="h-3 w-full" />
              <SkeletonLoader className="h-3 w-5/6" />
              <SkeletonLoader className="h-3 w-2/3" />
            </div>
          ))}
        </div>
        {/* Tabs */}
        <div className="glass-card">
          <div className="flex gap-4 px-4 py-3 border-b border-glass-200/10">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-4 w-16" />
            ))}
          </div>
          <div className="p-4 md:p-5 space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <SkeletonLoader key={i} className="h-14 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !partnership) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="PR Management"
          title="Partnership"
          onBack={() => navigate('/partnerships')}
          backLabel="Partnerships"
        />
        <EmptyState
          tone="danger"
          icon={AlertCircle}
          title="Failed to load partnership"
          description={error || 'Partnership not found'}
          action={
            <>
              <button onClick={loadPartnership} className="glass-button">Retry</button>
              <button
                onClick={() => navigate('/partnerships')}
                className="glass-button-secondary"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Partnerships
              </button>
            </>
          }
        />
      </div>
    );
  }

  const modpack = partnership.modpack;
  const creator = partnership.creator;
  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        className="relative z-10"
        eyebrow="PR Management"
        onBack={() => navigate('/partnerships')}
        backLabel="Partnerships"
        title={
          <span className="inline-flex items-center gap-3 max-w-full">
            {modpack?.logo_url && (
              <img
                src={modpack.logo_url}
                alt={modpack.name}
                className="w-9 h-9 md:w-10 md:h-10 rounded-md object-cover bg-dark-900/50 flex-shrink-0 ring-1 ring-glass-200/15"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            {modpack ? (
              <button
                type="button"
                className="min-w-0 truncate text-left hover:text-accent-300 transition-colors"
                onClick={() => setShowModpackDetail(true)}
                title="View modpack details"
              >
                {modpack.name}
              </button>
            ) : (
              <button
                type="button"
                className="min-w-0 truncate text-left hover:text-accent-300 transition-colors"
                onClick={() => creator && setShowCreatorDetail(true)}
                title="View creator details"
              >
                {creator?.name || 'Unknown Partnership'}
              </button>
            )}
          </span>
        }
        titleAdornment={
          <>
            {/* Status */}
            <div className="relative flex items-center gap-1">
              <PartnershipStatusBadge status={partnership.status} size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  disabled={statusChanging}
                  className="btn-ghost btn-xs"
                  title="Change status"
                  aria-label="Change status"
                  aria-expanded={showStatusDropdown}
                >
                  {statusChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                  Change
                </button>
              )}
              {showStatusDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[180px] py-1">
                  {PARTNERSHIP_STATUSES.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleStatusChange(s)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm hover:bg-glass-200/10 transition-colors',
                        s === partnership.status ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200'
                      )}
                    >
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Priority */}
            <div className="relative flex items-center gap-1">
              <PartnershipPriorityBadge priority={partnership.priority} size="md" />
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setShowPriorityDropdown(!showPriorityDropdown)}
                  disabled={priorityChanging}
                  className="btn-icon btn-icon-sm"
                  title="Change priority"
                  aria-label="Change priority"
                  aria-expanded={showPriorityDropdown}
                >
                  {priorityChanging ? <Loader className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                </button>
              )}
              {showPriorityDropdown && (
                <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-50 min-w-[120px] max-h-[300px] overflow-y-auto py-1">
                  {Array.from({ length: 10 }, (_, i) => i + 1).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => handlePriorityChange(p)}
                      className={cn(
                        'w-full text-left px-3 py-1.5 text-sm tabular-nums hover:bg-glass-200/10 transition-colors',
                        p === partnership.priority ? 'text-accent-300 bg-accent-500/10' : 'text-glass-200'
                      )}
                    >
                      Priority {p}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        }
        description={
          modpack ? (
            creator ? (
              <>
                by{' '}
                <button
                  type="button"
                  onClick={() => setShowCreatorDetail(true)}
                  className="text-glass-200 font-medium hover:text-accent-300 transition-colors"
                >
                  {creator.name}
                </button>
                <span className="text-glass-500 ml-1">({creator.type})</span>
              </>
            ) : undefined
          ) : (
            <>
              Creator-level partnership
              {creator && <span className="ml-1">({creator.type})</span>}
            </>
          )
        }
        actions={
          <>
            <button onClick={handleCopyId} className="glass-button-secondary" title="Copy ID" aria-label="Copy partnership ID">
              {copiedId ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              {copiedId ? 'Copied' : 'ID'}
            </button>
            {canEdit && (
              <button onClick={() => setIsEditOpen(true)} className="glass-button">
                <Pencil className="w-4 h-4" />
                Edit
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => setIsDeleteOpen(true)}
                className="btn-danger"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            )}
          </>
        }
      >
        {/* Assignee + Tags */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <span className="eyebrow">Assignee</span>
            {partnership.assigned_to ? (
              <>
                <PlayerProfile uuid={partnership.assigned_to} inline showUuid={false} />
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => {
                      setAssigneeSearchQuery('');
                      setShowAssigneeInput(true);
                    }}
                    className="btn-icon btn-icon-sm"
                    title="Change assignee"
                    aria-label="Change assignee"
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                )}
              </>
            ) : canEdit ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => {
                    const myUuid = identity?.raw?.custom?.minecraft_uuid;
                    if (myUuid) handleAssigneeChange(myUuid);
                  }}
                  disabled={assigneeChanging || !identity?.raw?.custom?.minecraft_uuid}
                  className="btn-ghost btn-xs text-accent-300 hover:text-accent-200"
                >
                  {assigneeChanging ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                  Assign me
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setAssigneeSearchQuery('');
                    setShowAssigneeInput(true);
                  }}
                  className="btn-ghost btn-xs"
                >
                  <Search className="w-3.5 h-3.5" />
                  Search
                </button>
              </div>
            ) : (
              <span className="text-xs text-glass-500">Unassigned</span>
            )}
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="eyebrow mr-0.5">Tags</span>
            {partnership.tags?.map((tag) => (
              <span
                key={tag.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border group/tag"
                style={{
                  backgroundColor: `${tag.color}20`,
                  borderColor: `${tag.color}40`,
                  color: tag.color,
                }}
              >
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                {tag.name}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag.id)}
                    disabled={tagActionLoading === tag.id}
                    className="ml-0.5 opacity-0 group-hover/tag:opacity-100 focus-visible:opacity-100 transition-opacity hover:brightness-125"
                    title="Remove tag"
                    aria-label={`Remove tag ${tag.name}`}
                  >
                    {tagActionLoading === tag.id ? (
                      <Loader className="w-3 h-3 animate-spin" />
                    ) : (
                      <X className="w-3 h-3" />
                    )}
                  </button>
                )}
              </span>
            ))}
            {!partnership.tags?.length && !canEdit && (
              <span className="text-xs text-glass-500">None</span>
            )}
            {canEdit && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTagPicker(!showTagPicker)}
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border border-dashed border-glass-200/25 text-glass-400 hover:text-white hover:border-glass-200/40 transition-colors"
                  aria-expanded={showTagPicker}
                >
                  <Plus className="w-3 h-3" />
                  Tag
                </button>
                {showTagPicker && (
                  <div className="absolute top-full left-0 mt-1 glass-card overflow-hidden z-20 min-w-[180px] max-h-[200px] overflow-y-auto py-1">
                    {(() => {
                      const currentTagIds = new Set(partnership.tags?.map(t => t.id) || []);
                      const available = allTags.filter(t => !currentTagIds.has(t.id));
                      if (available.length === 0) {
                        return (
                          <div className="px-3 py-2 text-xs text-glass-500">
                            No more tags available
                          </div>
                        );
                      }
                      return available.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => {
                            handleAddTag(tag.id);
                            setShowTagPicker(false);
                          }}
                          disabled={tagActionLoading === tag.id}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-glass-200/10 transition-colors flex items-center gap-2"
                        >
                          <span
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{ backgroundColor: tag.color }}
                          />
                          <span className="text-glass-200">{tag.name}</span>
                        </button>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {showAssigneeInput && (
          <div className="glass-card p-3 w-full max-w-sm">
            <div className="flex items-center gap-2 mb-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-glass-400 pointer-events-none" />
                <input
                  type="text"
                  value={assigneeSearchQuery}
                  onChange={(e) => setAssigneeSearchQuery(e.target.value)}
                  className="glass-input pl-9 pr-8 w-full"
                  placeholder="Search by username…"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowAssigneeInput(false);
                      setAssigneeSearchQuery('');
                    }
                  }}
                />
                {assigneeSearchLoading && (
                  <Loader className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-glass-400" />
                )}
              </div>
              {identity?.raw?.custom?.minecraft_uuid && identity.raw.custom.minecraft_uuid !== partnership.assigned_to && (
                <button
                  type="button"
                  onClick={() => {
                    handleAssigneeChange(identity.raw.custom.minecraft_uuid);
                    setAssigneeSearchQuery('');
                  }}
                  disabled={assigneeChanging}
                  className="glass-button whitespace-nowrap"
                >
                  Me
                </button>
              )}
              {partnership.assigned_to && (
                <button
                  type="button"
                  onClick={() => handleAssigneeChange(null)}
                  disabled={assigneeChanging}
                  className="btn-danger whitespace-nowrap"
                >
                  Unassign
                </button>
              )}
              <button
                type="button"
                onClick={() => {
                  setShowAssigneeInput(false);
                  setAssigneeSearchQuery('');
                }}
                className="btn-icon"
                title="Close"
                aria-label="Close assignee search"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {assigneeStaffSearchError && (
              <p className="text-xs text-red-400 px-1">{assigneeStaffSearchError}</p>
            )}
            {assigneeSearchQuery.length > 0 && assigneeSearchQuery.length < 2 && (
              <p className="text-xs text-glass-500 px-1">Type at least 2 characters…</p>
            )}
            {assigneeSearchResults.length > 0 && (
              <div className="space-y-0.5 max-h-[200px] overflow-y-auto">
                {assigneeSearchResults.map((player) => (
                  <button
                    key={player.uuid}
                    type="button"
                    onClick={() => {
                      handleAssigneeChange(player.uuid);
                      setAssigneeSearchQuery('');
                    }}
                    disabled={assigneeChanging}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-left hover:bg-glass-200/10 transition-colors',
                      player.uuid === partnership.assigned_to && 'bg-accent-500/10'
                    )}
                  >
                    <PlayerProfile uuid={player.uuid} name={player.name} inline showUuid={false} />
                    <span className="text-[11px] text-glass-500 font-mono flex-shrink-0">{player.uuid.slice(0, 8)}…</span>
                  </button>
                ))}
              </div>
            )}
            {debouncedAssigneeSearch.length >= 2 && !assigneeSearchLoading && !assigneeStaffSearchError && assigneeSearchResults.length === 0 && (
              <p className="text-xs text-glass-500 px-1 py-2">No staff found</p>
            )}
          </div>
        )}
      </PageHeader>

      {/* Tabs */}
      <DetailSections sections={detailSections('partnership', TABS, { notes: partnership.notes_count, comments: partnership.comments_count, codes: partnership.codes?.length, incentives: partnership.incentives?.length, servers: partnership.servers?.length })} active={activeTab} onChange={setActiveTab}>
        {activeTab === 'overview' && (<>      {/* Info Cards */}
      <div className={cn('grid gap-4', isMobile ? 'grid-cols-1' : 'grid-cols-2')}>
        {/* Summary & Deal */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Summary" icon={FileText} />
          <p className="text-sm text-glass-200 whitespace-pre-wrap">
            {partnership.summary || 'No summary provided'}
          </p>
          {partnership.deal_terms && (
            <div className="mt-4">
              <div className="eyebrow mb-1.5">Deal Terms</div>
              <pre className="subcard p-3 text-xs font-mono text-glass-300 overflow-x-auto">
                {JSON.stringify(JSON.parse(partnership.deal_terms), null, 2)}
              </pre>
            </div>
          )}
          {partnership.decline_reason && (
            <div className="alert alert-danger mt-4">
              <AlertCircle />
              <div className="min-w-0">
                <div className="eyebrow text-red-300/80 mb-0.5">Decline Reason</div>
                <p className="text-sm">{partnership.decline_reason}</p>
              </div>
            </div>
          )}
        </div>

        {/* Dates & Metadata */}
        <div className="glass-card p-4 md:p-5">
          <SectionHeader title="Key Dates" icon={Calendar} />
          <dl className="space-y-2 text-sm">
            {([
              ['Contact Date', partnership.contact_date],
              ['Response Date', partnership.response_date],
              ['Start Date', partnership.start_date],
              ['End Date', partnership.end_date],
            ] as [string, number | null][]).map(([label, val]) => (
              <div key={label} className="flex justify-between gap-3">
                <dt className="text-glass-400">{label}</dt>
                <dd className="text-glass-200 tabular-nums">{formatDate(val)}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 pt-3 border-t border-glass-200/10 space-y-1 text-xs text-glass-500">
            <div className="flex justify-between gap-3">
              <span>Created</span>
              <span className="tabular-nums text-right">
                {formatDate(partnership.created_at)} by{' '}
                <CreatorProfile creatorId={partnership.created_by} inline />
              </span>
            </div>
            {partnership.updated_at && (
              <div className="flex justify-between gap-3">
                <span>Updated</span>
                <span className="tabular-nums text-right">
                  {formatDistanceToNow(new Date(partnership.updated_at), { addSuffix: true })}
                  {partnership.updated_by && (
                    <> by <CreatorProfile creatorId={partnership.updated_by} inline /></>
                  )}
                </span>
              </div>
            )}
          </div>

          {/* Creator / Modpack quick info */}
          {modpack?.platform_url && (
            <div className="mt-3 pt-3 border-t border-glass-200/10">
              <a
                href={modpack.platform_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link inline-flex items-center gap-1 text-xs"
              >
                <ExternalLink className="w-3 h-3" />
                View on {modpack.platform_type || 'Platform'}
              </a>
            </div>
          )}

          {/* Apogee enrichment — live download stats from Modpack Index */}
          {modpack && (
            <div className="mt-3 pt-3 border-t border-glass-200/10">
              <ApogeePackEnrichment
                packName={modpack.name}
                serverFqdn={partnership.servers?.[0]?.fqdn || undefined}
                mode="inline"
              />
            </div>
          )}
        </div>
      </div>

</>)}
        {activeTab === 'workspaces' && (<>      {/* Linked Kanban Cards */}
      <KanbanLinkedCardsSection linkType="partnership" targetId={partnership.id} title="Linked Kanban Cards" />

</>)}

          {activeTab === 'notes' && (
            <PartnershipNotesSection
              partnershipId={partnership.id}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'comments' && (
            <PartnershipCommentsSection
              partnershipId={partnership.id}
              canCreate={canCreate}
              canDelete={canDelete}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'codes' && (
            <PartnershipCodesSection
              partnershipId={partnership.id}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'incentives' && (
            <PartnershipIncentivesSection
              partnershipId={partnership.id}
              canCreate={canCreate}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'servers' && (
            <PartnershipServersSection
              partnershipId={partnership.id}
              canEdit={canEdit}
              canDelete={canDelete}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'calendar' && (
            <PartnershipCalendarSection
              partnershipId={partnership.id}
              canEdit={canEdit}
              onUpdate={loadPartnership}
            />
          )}
          {activeTab === 'activity' && (
            <PartnershipActivityLog partnershipId={partnership.id} />
          )}
          {activeTab === 'updates' && (
            <div className="space-y-4">
              <PartnerNoticeBar notice={partnerNotice} onDismiss={() => setPartnerNotice(null)} />
              {modpack ? (
                <PartnerReportList
                  query={{ modpack_id: modpack.id, status: 'all' }}
                  canEdit={canEdit}
                  canOpenConfigs={isDev || canViewModpackConfigs(identity)}
                  onNotice={setPartnerNotice}
                  emptyTitle={`No update reports for ${modpack.name} yet`}
                  emptyDescription="When the partner reports a new version from their channel, or it turns up on CurseForge first, it shows here."
                />
              ) : (
                <EmptyState bare compact icon={FileClock} title="This is a creator-level partnership" description="Update reports belong to a pack. The partner page lists every report from this creator." />
              )}
              {(partnership.creator_id ?? modpack?.creator_id) && (
                <Link to={partnerPagePath((partnership.creator_id ?? modpack?.creator_id) as string, 'updates')} className="link text-sm">
                  See every report from this partner, or log one
                </Link>
              )}
            </div>
          )}
          {activeTab === 'partner' && (
            (partnership.creator_id ?? modpack?.creator_id) ? (
              <PartnerSummaryCard creatorId={(partnership.creator_id ?? modpack?.creator_id) as string} />
            ) : (
              <EmptyState bare compact icon={UsersRound} title="This partnership has no creator" description="Partner teams belong to a creator. Edit the partnership to set one." />
            )
          )}

      </DetailSections>

      {/* Edit Modal */}
      {isEditOpen && (
        <EditPartnershipModal
          isOpen={isEditOpen}
          partnership={partnership}
          onClose={() => setIsEditOpen(false)}
          onSuccess={() => {
            setIsEditOpen(false);
            loadPartnership();
          }}
        />
      )}

      {/* Delete Confirmation */}
      {isDeleteOpen && (
        <ConfirmationModal
          isOpen={isDeleteOpen}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
          title="Delete Partnership"
          message="Are you sure you want to delete this partnership? This will also delete all associated notes, comments, codes, incentives, server links, and activity log entries. This action cannot be undone."
          confirmText="Delete"
          isLoading={isDeleting}
        />
      )}

      {/* Creator Detail Modal */}
      {showCreatorDetail && creator && (
        <CreatorDetailModal
          creatorId={creator.id}
          isOpen={showCreatorDetail}
          onClose={() => setShowCreatorDetail(false)}
          onUpdate={loadPartnership}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}

      {/* Modpack Detail Modal */}
      {showModpackDetail && modpack && (
        <ModpackDetailModal
          modpackId={modpack.id}
          isOpen={showModpackDetail}
          onClose={() => setShowModpackDetail(false)}
          onUpdate={loadPartnership}
          canEdit={canEdit}
          canDelete={canDelete}
        />
      )}
    </div>
  );
};
